import { Trophy } from 'lucide-react';
import { getTournamentType } from '../../../engine';
import type { LiveTournament } from '../memberData';
import { Card, EmptyState, SectionTitle } from '../mobileUi';
import { useT } from '../../../i18n';
import { LiveTournamentBlock } from './MobileMatches';

export interface MobileSeriespillProps {
  member: any;
  effectivePlayerDB: any[];
  /** Live seriespill only (MobileApp filters by tournament type). */
  live: LiveTournament[];
  /** Finished seriespill seasons from the history helper (already filtered). */
  past: any[];
  courtMeta?: any;
  calcPlayerPositionInTournament: (playerId: any, t: any) => any;
  recordKMResultForTournament?: (tid: any, updatedMatch: any) => void;
  /** Inside Kamper: no heading. */
  embedded?: boolean;
}

// «Seriespill» — league play gets its own tab: the seasons in progress with
// the member's matches (result entry through the shared KM recorder) and the
// finished seasons with placement. Kept out of Hjem and Kamper on purpose.
export function MobileSeriespill({
  member,
  effectivePlayerDB,
  live,
  past,
  courtMeta,
  calcPlayerPositionInTournament,
  recordKMResultForTournament,
  embedded = false,
}: MobileSeriespillProps) {
  const t = useT();
  const db = effectivePlayerDB || [];
  return (
    <div className={embedded ? 'space-y-4' : 'px-4 pb-4 space-y-4'}>
      {!embedded && (
        <h1
          className="font-display font-semibold text-ink leading-tight pt-2"
          style={{ fontSize: 26 }}
        >
          {t('mobile.league.title')}
        </h1>
      )}

      <div>
        <SectionTitle>{t('mobile.league.ongoing')}</SectionTitle>
        {live.length === 0 ? (
          <EmptyState
            icon={<Trophy size={22} />}
            title={t('mobile.league.emptyTitle')}
            hint={t('mobile.league.emptyHint')}
          />
        ) : (
          <div className="space-y-4">
            {live.map((l) => (
              <LiveTournamentBlock
                key={l.key}
                live={l}
                member={member}
                db={db}
                courtMeta={courtMeta}
                recordKMResultForTournament={recordKMResultForTournament}
              />
            ))}
          </div>
        )}
      </div>

      {past.length > 0 && (
        <div>
          <SectionTitle>{t('mobile.league.pastSeasons')}</SectionTitle>
          <div className="space-y-2">
            {past.map((h: any) => {
              const tourn = h.tournament || {};
              const tt = getTournamentType(tourn.config || tourn.type);
              const pos = h.finalPosition ?? calcPlayerPositionInTournament(member?.id, tourn);
              const position = typeof pos === 'number' ? pos : (pos?.position ?? null);
              const total = typeof pos === 'object' && pos ? pos.total : null;
              return (
                <Card key={h.key} accent={tt?.color || '#14b8a6'}>
                  <div className="flex items-center gap-3">
                    <div
                      className="flex-shrink-0 flex items-center justify-center rounded-2xl font-display font-semibold"
                      style={{ width: 46, height: 46, background: '#eef0ea', fontSize: 18 }}
                    >
                      {position ? `${position}.` : '–'}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[14px] font-semibold text-ink truncate">
                        {tourn.config?.name || tourn.name || t('mobile.league.title')}
                      </div>
                      <div className="text-[11px] text-ink-3">
                        {t('mobile.league.matchCount', { count: (h.matches || []).length })}
                        {position && total ? t('mobile.league.ofTotal', { total }) : ''}
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
